import { Link } from 'react-router-dom';
import { Globe, ShieldCheck, Handshake, MapPin, Users, MessageCircle, Heart } from 'lucide-react';
import AppStoreBadges from '@/components/AppStoreBadges';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Marketing', path: '/marketing' },
      { name: 'Contact', path: '/contact' },
      { name: 'Support', path: '/support' },
      { name: 'FAQ', path: '/faq' }
    ],
    community: [
      { name: 'Events', path: '/events' },
      { name: 'Groups', path: '/groups' },
      { name: 'Resources', path: '/guides' },
      { name: 'Find Expats', path: '/find-expats' },
      { name: 'Job Board', path: '/jobs' },
      { name: 'Business Directory', path: '/businesses' }
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms & Conditions', path: '/terms' }
    ]
  };

  return (
    <footer className="bg-foreground text-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/a4035128-ba5f-41e2-8c85-a2f1cef97a56/images/c96ad2beeee3b28854016505e6ee1acce69f2fb6e7cdac0f802648d487e720c3.png" 
                  alt="ExpatsSphere"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <span className="text-lg font-heading font-bold">
                  Expats<span className="italic text-accent">Sphere</span>
                </span>
                <span className="block text-[11px] text-background/50 leading-none mt-0.5" data-testid="footer-operator-attribution">
                  by Medpax, LLC
                </span>
              </div>
            </Link>
            <p className="text-background/70 text-sm mb-4">
              Connecting expats globally. Find your people, anywhere in the world.
            </p>
            <div className="flex items-center gap-1 text-sm text-background/60">
              <Globe className="w-4 h-4" />
              <span>Available worldwide</span>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-background/70 hover:text-accent transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Community Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">Community</h3>
            <ul className="space-y-3">
              {footerLinks.community.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-background/70 hover:text-accent transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-background/70 hover:text-accent transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Get the App Section */}
            <div className="mt-8">
              <h3 className="font-bold text-lg mb-4">Get the App</h3>
              <AppStoreBadges size="sm" className="flex-col" />
            </div>
          </div>
        </div>

        {/* Community Guidelines */}
        <div className="border-t border-white/10 mt-10 pt-8" data-testid="community-guidelines">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Code of Conduct */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Handshake className="w-5 h-5 text-accent" />
                <h3 className="font-bold text-base text-background">The Code of Conduct</h3>
              </div>
              <p className="text-background/60 text-xs italic mb-3">
                Finding your people starts with mutual respect.
              </p>
              <ul className="space-y-2 text-sm text-background/70">
                <li className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                  <span><strong className="text-background/90">Be Inclusive:</strong> We celebrate all cultures. Respect differences in background, language, and tradition.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Heart className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                  <span><strong className="text-background/90">Share, Don't Sell:</strong> We're here for friendships, not sales pitches. Please avoid "cold DMing" members for business or commercial offers.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                  <span><strong className="text-background/90">Be a Top-Tier Human:</strong> We have a zero-tolerance policy for harassment, bullying, or hate speech.</span>
                </li>
              </ul>
            </div>

            {/* Safety First */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold text-base text-background">Safety First: Meeting Offline</h3>
              </div>
              <p className="text-background/60 text-xs italic mb-3">
                Stay "Expat-Smart" when meeting someone new in the real world.
              </p>
              <ul className="space-y-2 text-sm text-background/70">
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5 shrink-0 text-xs font-bold">1.</span>
                  <span><strong className="text-background/90">Meet in Public:</strong> Choose a busy cafe, park, or restaurant — never a private residence.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5 shrink-0 text-xs font-bold">2.</span>
                  <span><strong className="text-background/90">The Buddy System:</strong> Tell a friend where you're going and who you are meeting.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5 shrink-0 text-xs font-bold">3.</span>
                  <span><strong className="text-background/90">Keep it on the App:</strong> Use the ExpatsSphere chat until you're 100% comfortable sharing personal info.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5 shrink-0 text-xs font-bold">4.</span>
                  <span><strong className="text-background/90">Trust Your Gut:</strong> If a person or a situation feels "off," it's okay to leave. Your safety is the priority.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Report notice */}
          <div className="mt-4 text-center">
            <p className="text-background/50 text-xs">
              <MessageCircle className="w-3 h-3 inline mr-1" />
              See something? Say something. Use the <strong className="text-background/70">Report</strong> button on any profile, or email{' '}
              <a href="mailto:support@expatssphere.com" className="text-accent hover:underline">support@expatssphere.com</a>
            </p>
          </div>
        </div>

        {/* Copyright Notice */}
        <div className="border-t border-white/10 mt-6 pt-6 text-center">
          <p className="text-background/50 text-xs">
            © {currentYear} Medpax, LLC. ExpatsSphere™ is a trademark of Medpax, LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
